import React from 'react';
import { ChevronDown, ChevronUp, Minus, Plus, Users } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

const PT_CATALOG = [
  { id: 'pt-1', label: 'Single PT Session', price: 90 },
  { id: 'pt-4', label: '4 Pack PT Session', price: 290 },
  { id: 'pt-8', label: '8-Pack PT Session', price: 490 },
  { id: 'pt-12', label: '12-Pack PT Sessions', price: 590 },
];

const ADDON_CATALOG = [
  { id: 'tanning', label: 'Tanning', price: 25 },
  { id: 'kid-1', label: 'KID CARE - 1 CHILD', price: 15 },
  { id: 'kid-2', label: 'KID CARE - 2 CHILDREN', price: 20 },
  { id: 'kid-3', label: 'KID CARE - 3 CHILDREN', price: 25 },
  { id: 'kid-4', label: 'KID CARE - 4 CHILDREN', price: 30 },
  { id: 'kid-5', label: 'KID CARE - 5 CHILDREN', price: 35 },
];

// Single source of truth for every plan in the checkout flow.
// `extraMemberFee` is the per-additional-member surcharge added on top
// of the base `signUpDues` and (for monthly plans) `monthlyDues`.
// `maxMembers` caps the stepper.
export const PLANS = {
  '1-year-individual': {
    title: '1-Year Individual Membership',
    description: '1-Year Individual Membership with monthly payment option.',
    enrollmentFee: 26,
    signUpDues: 49,
    monthlyDues: 49,
    termMonths: 12,
    monthlyLabel: 'Monthly Dues',
    showPurchaseItems: true,
    ptCatalog: PT_CATALOG,
    addOns: ADDON_CATALOG,
    membersConfigurable: false,
    renewalCopy:
      'After the initial term period is completed, the membership will automatically renew with the following payments.',
  },
  '1-year-family': {
    title: '1-Year Family Membership',
    description:
      '1-Year Family Membership – First person added is $49, price increases with each additional member.',
    enrollmentFee: 26,
    signUpDues: 49,
    monthlyDues: 49,
    termMonths: 12,
    monthlyLabel: 'Monthly Dues',
    showPurchaseItems: true,
    ptCatalog: PT_CATALOG,
    addOns: ADDON_CATALOG,
    membersConfigurable: true,
    extraMemberFee: 20,   // $20/mo per additional member
    maxMembers: 6,
    renewalCopy:
      'After the initial term period is completed, the family membership will automatically renew with the following payments.',
  },
  '1-year-paid-in-full': {
    title: '1-Year Paid in Full Membership - Family',
    description:
      'Pay once for a full year — no monthly billing, no auto-renewal. Total scales as you add family members.',
    enrollmentFee: 0,
    signUpDues: 490,
    monthlyDues: 0,
    paidInFull: true,
    termMonths: 12,
    showPurchaseItems: false,
    ptCatalog: [],
    addOns: ADDON_CATALOG.filter((a) => a.id === 'tanning'),
    membersConfigurable: true,
    extraMemberFee: 200,  // $200 one-time per additional member
    maxMembers: 6,
    renewalCopy:
      'No automatic billing. You\'ll receive a renewal reminder before your term ends — renew on your own schedule at the same paid-in-full rate.',
  },
};

export const money = (n) => `$${Number(n || 0).toFixed(2)}`;

export const fmtDate = (d) =>
  d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

/** Compute effective Sign-Up Dues + Monthly Dues for a plan given member count. */
export function effectivePricing(plan, members = 1) {
  if (!plan.membersConfigurable) {
    return { signUpDues: plan.signUpDues, monthlyDues: plan.monthlyDues };
  }
  const extras = Math.max(0, members - 1) * (plan.extraMemberFee || 0);
  return {
    signUpDues: plan.monthlyDues > 0 ? plan.signUpDues + extras : plan.signUpDues + extras,
    monthlyDues: plan.monthlyDues > 0 ? plan.monthlyDues + extras : 0,
  };
}

export const SummaryRow = React.memo(function SummaryRow({ label, value, bold = false }) {
  return (
    <div className={`flex items-center justify-between gap-3 py-1.5 text-sm ${bold ? 'font-bold text-white' : 'text-zinc-300'}`}>
      <span className="min-w-0 break-words">{label}</span>
      <span className="tabular-nums flex-shrink-0 whitespace-nowrap">{value}</span>
    </div>
  );
});

/**
 * Family-size stepper — used by Family Plan + Paid-in-Full pages.
 * Controlled (value/onChange). max defaults to plan.maxMembers.
 */
export function MemberStepper({ value, onChange, max = 6, plan }) {
  const dec = () => onChange(Math.max(1, value - 1));
  const inc = () => onChange(Math.min(max, value + 1));
  const surcharge = (plan.extraMemberFee || 0) * Math.max(0, value - 1);
  return (
    <div className="bg-zinc-950 border border-red-900/40 rounded-md p-5" data-testid="member-stepper">
      <div className="flex items-center gap-3 mb-3">
        <Users className="w-4 h-4 text-red-500" />
        <h3 className="text-[11px] font-bold tracking-[0.3em] text-red-500 uppercase">Family Members</h3>
      </div>
      <p className="text-xs text-zinc-500 mb-4 leading-relaxed">
        +{money(plan.extraMemberFee || 0)} {plan.monthlyDues > 0 ? '/ month' : 'one-time'} for each additional member after the first.
      </p>
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={dec}
            disabled={value <= 1}
            data-testid="stepper-dec"
            aria-label="Remove a member"
            className="flex items-center justify-center w-10 h-10 rounded-full border-2 border-red-700/60 text-white hover:border-red-500 hover:bg-red-700/20 disabled:opacity-40 disabled:cursor-not-allowed transition"
          >
            <Minus className="w-4 h-4" />
          </button>
          <div className="min-w-[60px] text-center">
            <div data-testid="stepper-value" className="font-display text-3xl text-white tabular-nums leading-none">{value}</div>
            <div className="text-[9px] tracking-[0.25em] text-zinc-500 mt-1">{value === 1 ? 'MEMBER' : 'MEMBERS'}</div>
          </div>
          <button
            type="button"
            onClick={inc}
            disabled={value >= max}
            data-testid="stepper-inc"
            aria-label="Add a member"
            className="flex items-center justify-center w-10 h-10 rounded-full border-2 border-red-700/60 text-white hover:border-red-500 hover:bg-red-700/20 disabled:opacity-40 disabled:cursor-not-allowed transition"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
        {surcharge > 0 && (
          <div className="text-right">
            <div className="text-[9px] tracking-[0.3em] text-zinc-500">EXTRA MEMBERS</div>
            <div className="font-display text-xl text-red-500 tabular-nums">+{money(surcharge)}</div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Plan summary sidebar — DARK THEME (black/white/red).
 * Used on both step 1 (options) and step 2 (member info).
 */
export function PlanSummary({
  plan,
  members = 1,
  ptItem,
  addOnsTotal,
  addOnsLabel = 'Add-Ons',
  promo,
  promoApplied = false,
  promoDiscount = 0,
  firstDueDate,
  showRenewal,
  onToggleRenewal,
  testIdPrefix = 'summary',
}) {
  const { signUpDues, monthlyDues } = effectivePricing(plan, members);
  const dueTodaySubtotal =
    (plan.enrollmentFee || 0) + signUpDues + (ptItem?.price || 0) + (addOnsTotal || 0);
  const dueTodayTotal = Math.max(0, dueTodaySubtotal - promoDiscount);
  const perPaymentTotal = monthlyDues + (monthlyDues > 0 ? addOnsTotal : 0);
  const remainingPayments =
    typeof plan.remainingPayments !== 'undefined'
      ? plan.remainingPayments
      : Math.max(0, plan.termMonths - 1);

  const cardBase = 'bg-zinc-950 border border-red-900/40 rounded-md overflow-hidden';
  const headBase = 'text-center text-sm font-bold text-white py-3 bg-black/60 border-b border-red-900/40';

  return (
    <aside className="space-y-5" data-testid={`${testIdPrefix}-aside`}>
      {/* Plan title */}
      <section className={cardBase}>
        <h2 className="text-center text-[15px] font-bold text-white py-4 border-b border-red-900/40 tracking-wide">
          {plan.title}
        </h2>
        <div className="p-5">
          <p className="text-sm text-zinc-400 leading-relaxed">{plan.description}</p>
          {plan.membersConfigurable && members > 1 && (
            <p className="mt-3 text-[10px] tracking-[0.25em] text-red-500 font-bold">
              {members} MEMBERS · +{money((plan.extraMemberFee || 0) * (members - 1))} {plan.monthlyDues > 0 ? '/ MO' : 'ONE-TIME'}
            </p>
          )}
        </div>
      </section>

      {/* Due Today */}
      <section className={cardBase} data-testid={`${testIdPrefix}-due-today`}>
        <h3 className={headBase}>{plan.paidInFull ? 'One-Time Payment' : 'Due Today'}</h3>
        <div className="p-5">
          {plan.enrollmentFee > 0 && <SummaryRow label="Enrollment Fee" value={money(plan.enrollmentFee)} />}
          <SummaryRow
            label={plan.paidInFull ? (members === 1 ? 'Full Year Membership' : `Full Year · ${members} members`) : 'Sign Up Dues'}
            value={money(signUpDues)}
          />
          {ptItem && <SummaryRow label={ptItem.label} value={money(ptItem.price)} />}
          {addOnsTotal > 0 && <SummaryRow label={addOnsLabel} value={money(addOnsTotal)} />}
          {promoApplied && <SummaryRow label={`Promo (${(promo || '').toUpperCase()})`} value={`-${money(promoDiscount)}`} />}
          <div className="border-t border-red-900/40 mt-3 pt-3">
            {!plan.paidInFull && <SummaryRow label="Subtotal" value={money(dueTodaySubtotal)} />}
            <SummaryRow label={plan.paidInFull ? 'Total' : 'Total Due Today'} value={money(dueTodayTotal)} bold />
          </div>
        </div>
      </section>

      {/* === Middle section: monthly dues OR (paid-in-full) membership term === */}
      {plan.paidInFull ? (
        <section className={cardBase} data-testid={`${testIdPrefix}-term`}>
          <h3 className={headBase}>Membership Term</h3>
          <div className="p-5">
            <SummaryRow label="Term Length" value="12 months" />
            <SummaryRow label="Starts" value={fmtDate(new Date())} />
            <SummaryRow label="Ends" value={fmtDate(new Date(new Date().setFullYear(new Date().getFullYear() + 1)))} />
            <div className="border-t border-red-900/40 mt-3 pt-3">
              <SummaryRow label="Auto-Renewal" value="No" />
              <SummaryRow label="Monthly Billing" value="None" bold />
            </div>
            <p className="mt-4 text-xs text-zinc-500 leading-relaxed">
              Single annual payment. You won't be charged again until you choose to renew.
            </p>
          </div>
        </section>
      ) : (
        <section className={cardBase} data-testid={`${testIdPrefix}-monthly`}>
          <h3 className={headBase}>{plan.monthlyLabel || 'Monthly Dues'}</h3>
          <div className="p-5">
            <SummaryRow label="Payment Dues" value={money(monthlyDues)} />
            {monthlyDues > 0 && addOnsTotal > 0 && <SummaryRow label="Add-Ons" value={money(addOnsTotal)} />}
            <div className="border-t border-red-900/40 mt-3 pt-3">
              <SummaryRow label="First Due Date" value={fmtDate(firstDueDate)} />
              <SummaryRow label="Remaining Payments" value={remainingPayments} />
            </div>
            <div className="border-t border-red-900/40 mt-3 pt-3">
              <SummaryRow label="Subtotal" value={money(perPaymentTotal)} />
              <SummaryRow label="Per Payment Total" value={money(perPaymentTotal)} bold />
            </div>
          </div>
        </section>
      )}

      {/* Dues After Initial Term */}
      <section className={cardBase} data-testid={`${testIdPrefix}-renewal`}>
        <button
          type="button"
          onClick={onToggleRenewal}
          className="w-full flex items-center justify-center gap-2 text-sm font-bold text-white py-3 bg-black/60 border-b border-red-900/40"
        >
          {plan.paidInFull ? 'After Your Year Ends' : 'Dues After Initial Term'}
          {showRenewal ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        {showRenewal && (
          <div className="p-5">
            <p className="text-sm text-zinc-400 leading-relaxed">{plan.renewalCopy}</p>
            {plan.paidInFull ? (
              <div className="border-t border-red-900/40 mt-4 pt-3">
                <SummaryRow label="Annual Renewal Rate" value={money(signUpDues)} bold />
              </div>
            ) : (
              <>
                <div className="border-t border-red-900/40 mt-4 pt-3">
                  <SummaryRow label="Payment Dues" value={money(monthlyDues || signUpDues)} />
                </div>
                <div className="border-t border-red-900/40 mt-3 pt-3">
                  <SummaryRow label="First Due Date" value="On renewal" />
                  <SummaryRow label="Per Payment Total" value={money(monthlyDues || signUpDues)} bold />
                </div>
              </>
            )}
          </div>
        )}
      </section>
    </aside>
  );
}

/**
 * Dark header bar with FitnessPro logo + back link.
 *
 * The back link always uses React Router's <Link>. When the target is a
 * hash on the home page (e.g. `/#memberships`), we attach a click handler
 * that performs an in-app navigate + smooth scroll instead of a full
 * page reload — full reloads are unreliable in mobile webviews and can
 * surface as Chrome's "Can't open this page" error.
 */
export function CheckoutHeader({ backTo = '/#memberships', backLabel = 'BACK TO PLANS' }) {
  const navigate = useNavigate();
  const isHashRoute = backTo.startsWith('/#') || backTo.startsWith('#');

  const handleBack = (e) => {
    if (!isHashRoute) return;
    e.preventDefault();
    e.stopPropagation();
    const hashIdx = backTo.indexOf('#');
    const path = hashIdx === 0 ? '/' : backTo.slice(0, hashIdx) || '/';
    const id = backTo.slice(hashIdx + 1);
    // Navigate (no full reload) — then scroll to the section once it mounts.
    navigate(path + (id ? `#${id}` : ''));
    if (id) {
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          const top = el.getBoundingClientRect().top + window.pageYOffset - 96;
          window.scrollTo({ top, behavior: 'smooth' });
        }
      }, 80);
    } else {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
  };

  return (
    <header className="bg-black/95 backdrop-blur border-b border-red-900/40 sticky top-0 z-30">
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center gap-3 sm:gap-4">
        <Link
          to={backTo}
          onClick={handleBack}
          data-testid="checkout-back"
          className="inline-flex items-center gap-1.5 sm:gap-2 text-[11px] sm:text-[12px] tracking-[0.18em] sm:tracking-[0.25em] text-zinc-400 hover:text-red-500 transition font-bold whitespace-nowrap"
        >
          ← {backLabel}
        </Link>
        <div className="ml-auto flex items-center gap-2 sm:gap-3 min-w-0">
          <span className="logo-pulse-once w-8 h-8 sm:w-10 sm:h-10 flex-shrink-0">
            <img
              src="/logo.png"
              alt="FitnessPro Galt"
              className="w-full h-full object-contain drop-shadow-[0_0_12px_rgba(239,43,43,0.45)] transition-transform duration-300 ease-out hover:rotate-[5deg] hover:scale-110"
              loading="eager"
            />
          </span>
          <div className="leading-tight min-w-0">
            <div className="font-display text-base sm:text-lg text-white whitespace-nowrap">FITNESS PRO</div>
            <div className="hidden min-[360px]:block text-[9px] tracking-[0.3em] text-zinc-500 whitespace-nowrap">GALT · SINCE 1992</div>
          </div>
        </div>
      </div>
    </header>
  );
}
